import { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpFromBracket } from '@fortawesome/pro-solid-svg-icons/faArrowUpFromBracket';
import { faFileLines } from '@fortawesome/pro-solid-svg-icons/faFileLines';
import { faFileCircleXmark } from '@fortawesome/pro-solid-svg-icons/faFileCircleXmark';
import classnames from 'clsx';
import { FileUploader } from 'react-drag-drop-files';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';

import { FileStatus } from './constants';
import styles from './drag-drop-files.module.css';
import { messages } from './messages';
import { THEME_CONSTANTS } from '../../services/button-theme-service';

const defaultDropStyle = {
  border: 'none',
  opacity: '90%',
  backgroundColor: 'var(--grey-200)',
  fontWeight: '500',
  fontSize: '18px',
};

/**
 * Drag and drop file upload component
 * @param {object} props - Component props
 * @param {File|null} [props.file] - Initial file name to display
 * @param {string[]} props.allowedFiles - Array of allowed file extensions
 * @param {(file: File|null) => void} props.onChange - Callback function to set the selected file
 * @param {number} [props.maxSize] - Maximum file size in MB
 * @param {string} props.name - Name attribute for the file input
 * @param {string} [props.className] - Additional CSS class names
 * @param {object} [props.dropStyle] - Custom styles for the drop area
 * @returns {JSX.Element} Rendered component
 */
export const DragDropFiles = ({ allowedFiles, onChange, file, maxSize = 15, name, className = '', dropStyle = defaultDropStyle }) => {
  const [status, setStatus] = useState(file ? FileStatus.UPLOADED : FileStatus.NOT_UPLOADED);

  const handleChange = (f) => {
    if (f.size > 0) {
      setStatus(FileStatus.UPLOADED);
      onChange(f);
    } else {
      setStatus(FileStatus.MIN_SIZE_ERROR);
      onChange(null);
    }
  };

  const onSizeError = () => {
    setStatus(FileStatus.MAX_SIZE_ERROR);
    onChange(null);
  };

  const onTypeError = () => {
    setStatus(FileStatus.TYPE_ERROR);
    onChange(null);
  };

  const content = useMemo(() => {
    const link = (
      <Button className={styles['selected-file-btn']} theme={THEME_CONSTANTS.primary}>
        <FormattedMessage {...messages.helpTextAnotherFile} />
      </Button>
    );

    const fileTypesToShow = allowedFiles?.map((format) => format.toUpperCase()).join(', ');

    switch (status) {
      case FileStatus.NOT_UPLOADED:
        return (
          <>
            <FontAwesomeIcon className={styles.icon} icon={faArrowUpFromBracket} />
            <Text formattedMessageValues={{ types: fileTypesToShow }} textType={TextType.H4} value={messages.mainPrompt} />
            <Text className={styles['drag-drop-or']} textType={TextType.Paragraph} value={messages.or} />
            <Button className={styles['select-file-btn']} theme={THEME_CONSTANTS.primary}>
              <FormattedMessage {...messages.selectFile} />
            </Button>
          </>
        );
      case FileStatus.UPLOADED:
        return (
          <>
            <FontAwesomeIcon className={styles.icon} icon={faFileLines} />
            <Text textType={TextType.H4} value={file?.name} />
            <Text textType={TextType.H4} value={link} />
          </>
        );
      case FileStatus.MAX_SIZE_ERROR:
        return (
          <>
            <FontAwesomeIcon className={styles.icon} icon={faFileCircleXmark} />
            <Text formattedMessageValues={{ filesize: maxSize }} textType={TextType.H4} value={messages.fileTooBig} />
            <Text textType={TextType.H4} value={link} />
          </>
        );
      case FileStatus.MIN_SIZE_ERROR:
        return (
          <>
            <FontAwesomeIcon className={styles.icon} icon={faFileCircleXmark} />
            <Text textType={TextType.H4} value={messages.fileZeroSize} />
            <Text textType={TextType.H4} value={link} />
          </>
        );
      case FileStatus.TYPE_ERROR:
        return (
          <>
            <FontAwesomeIcon className={styles.icon} icon={faFileCircleXmark} />
            <Text formattedMessageValues={{ types: fileTypesToShow }} textType={TextType.H4} value={messages.fileTypeError} />
            <Text textType={TextType.H4} value={link} />
          </>
        );
      default:
        return null;
    }
  }, [allowedFiles, status, file?.name, maxSize]);

  return (
    <FileUploader
      classes={classnames(styles['drop-area'], className, {
        [styles.uploaded]: status !== FileStatus.NOT_UPLOADED,
      })}
      dropMessageStyle={dropStyle}
      handleChange={handleChange}
      maxSize={maxSize}
      name={name}
      onSizeError={onSizeError}
      onTypeError={onTypeError}
      types={allowedFiles}
    >
      <div className={styles['drag-drop-area-wrapper']}>{content}</div>
    </FileUploader>
  );
};
